import axios from 'axios';

/**
 * Gerência do ciclo de vida da sessão (BUG-010).
 *
 * Centraliza: armazenamento dos tokens com validade conhecida, refresh
 * PROATIVO (renova antes do access token expirar, para a sessão nunca cair
 * durante uso contínuo), single-flight na aba + exclusão mútua ENTRE ABAS
 * (Web Locks), coordenação via BroadcastChannel e logout limpo.
 *
 * Contexto: o access token tem TTL curto (15 min). Antes desta correção a
 * renovação era só reativa no 401, o que derrubava a sessão no boundary sob
 * concorrência (várias requisições/abas usando o mesmo refresh token, que é
 * rotacionado a cada /refresh no auth-service).
 */

const AUTH_URL = import.meta.env.VITE_AUTH_URL || 'http://localhost:3001/auth';

/** Renova este tanto de tempo ANTES do exp, para nunca chegar ao 401. */
const MARGEM_MS = 60_000;

const KEYS = {
  access: 'access_token',
  refresh: 'refresh_token',
  user: 'user',
  expiresAt: 'expires_at',
  deviceId: 'device_id',
} as const;

// ==========================================
// DEVICE ID
// ==========================================

function gerarUUID(): string {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

/** Obtém (ou gera e persiste) o Device ID usado no tracking de sessões. */
export function getDeviceId(): string {
  let deviceId = localStorage.getItem(KEYS.deviceId);
  if (!deviceId) {
    deviceId = gerarUUID();
    localStorage.setItem(KEYS.deviceId, deviceId);
  }
  return deviceId;
}

// ==========================================
// ESTADO DA ABA
// ==========================================

let timerRenovacao: ReturnType<typeof setTimeout> | null = null;
let renovacaoEmAndamento: Promise<string> | null = null;
let canal: BroadcastChannel | null = null;

function getCanal(): BroadcastChannel | null {
  if (typeof BroadcastChannel === 'undefined') return null;
  if (!canal) canal = new BroadcastChannel('palmer-auth');
  return canal;
}

function getExpiresAt(): number | null {
  const v = localStorage.getItem(KEYS.expiresAt);
  return v ? Number(v) : null;
}

function cancelarTimer(): void {
  if (timerRenovacao) {
    clearTimeout(timerRenovacao);
    timerRenovacao = null;
  }
}

// ==========================================
// TOKENS
// ==========================================

export interface TokensSessao {
  access_token: string;
  refresh_token: string;
  /** Validade do access token em segundos (do login/refresh). */
  expires_in?: number;
}

/**
 * Persiste os tokens, calcula a validade e (re)agenda o refresh proativo.
 * Por padrão notifica as outras abas para que reagendem com o novo `expires_at`.
 */
export function salvarTokens(tokens: TokensSessao, notificarAbas = true): void {
  localStorage.setItem(KEYS.access, tokens.access_token);
  localStorage.setItem(KEYS.refresh, tokens.refresh_token);
  if (tokens.expires_in && tokens.expires_in > 0) {
    localStorage.setItem(KEYS.expiresAt, String(Date.now() + tokens.expires_in * 1000));
  }
  agendarRenovacao();
  if (notificarAbas) getCanal()?.postMessage({ tipo: 'tokens' });
}

/**
 * Limpa a sessão e sinaliza a aplicação (evento `sessao:encerrada`) e as outras
 * abas para deslogar. Chamado quando o refresh falha de verdade (RT inválido/
 * revogado/expirado) ou no logout explícito.
 */
export function encerrarSessao(notificarAbas = true): void {
  cancelarTimer();
  localStorage.removeItem(KEYS.access);
  localStorage.removeItem(KEYS.refresh);
  localStorage.removeItem(KEYS.user);
  localStorage.removeItem(KEYS.expiresAt);
  window.dispatchEvent(new CustomEvent('sessao:encerrada'));
  if (notificarAbas) getCanal()?.postMessage({ tipo: 'logout' });
}

// ==========================================
// RENOVAÇÃO
// ==========================================

/**
 * (Re)agenda o refresh proativo para MARGEM_MS antes do exp. Sem `expires_at`
 * conhecido, não agenda nada (a rede de segurança é o refresh reativo no 401).
 */
export function agendarRenovacao(): void {
  cancelarTimer();
  if (!localStorage.getItem(KEYS.refresh)) return;
  const expiresAt = getExpiresAt();
  if (!expiresAt) return;
  const atraso = Math.max(expiresAt - MARGEM_MS - Date.now(), 0);
  timerRenovacao = setTimeout(() => {
    void renovarSessaoCoordenada();
  }, atraso);
}

/**
 * Executa o refresh de fato (single-flight dentro da aba). Retorna o novo
 * access token. Em falha real, encerra a sessão e propaga o erro.
 */
export function renovarSessao(): Promise<string> {
  if (renovacaoEmAndamento) return renovacaoEmAndamento;

  const refreshToken = localStorage.getItem(KEYS.refresh);
  if (!refreshToken) {
    encerrarSessao();
    return Promise.reject(new Error('Sem refresh token'));
  }

  renovacaoEmAndamento = axios
    .post(
      `${AUTH_URL}/refresh`,
      { refresh_token: refreshToken },
      { headers: { 'Content-Type': 'application/json', 'X-Device-Id': getDeviceId() } },
    )
    .then((resp) => {
      const { access_token, refresh_token, expires_in } = resp.data;
      salvarTokens({ access_token, refresh_token, expires_in });
      return access_token as string;
    })
    .catch((err) => {
      encerrarSessao();
      throw err;
    })
    .finally(() => {
      renovacaoEmAndamento = null;
    });

  return renovacaoEmAndamento;
}

/**
 * Renova com exclusão mútua ENTRE ABAS via Web Locks. Dentro do lock, revalida:
 * se outra aba já renovou (access token ainda válido por > MARGEM), reaproveita
 * em vez de disparar outro /refresh, evitando a corrida de refresh token
 * rotacionado. Sem Web Locks, cai no single-flight só da aba.
 */
export async function renovarSessaoCoordenada(): Promise<string> {
  const locks = (navigator as Navigator & { locks?: LockManager }).locks;
  if (!locks?.request) return renovarSessao();

  let token = '';
  await locks.request('palmer-auth-refresh', async () => {
    const atual = localStorage.getItem(KEYS.access);
    const expiresAt = getExpiresAt();
    if (atual && expiresAt && expiresAt - Date.now() > MARGEM_MS) {
      token = atual;
      return;
    }
    token = await renovarSessao();
  });
  return token;
}

/**
 * Liga a coordenação entre abas: reage a renovações e logouts vindos de outra
 * aba do mesmo navegador. Chamar uma vez no bootstrap da aplicação.
 */
export function iniciarCoordenacaoAbas(): void {
  const c = getCanal();
  if (!c) return;
  c.onmessage = (ev: MessageEvent) => {
    if (ev.data?.tipo === 'tokens') {
      // Outra aba renovou: reagenda com o novo expires_at (já no localStorage).
      agendarRenovacao();
    } else if (ev.data?.tipo === 'logout') {
      cancelarTimer();
      window.dispatchEvent(new CustomEvent('sessao:encerrada'));
    }
  };
}
